import React, { useState, useCallback, useRef, Suspense, useEffect } from 'react';
import { ChatInterface } from './components/ChatInterface';
import { IkigaiDashboard } from './components/IkigaiDashboard';
import { VoiceOverlay } from './components/VoiceOverlay';
import { SkillTreeVisualizer } from './components/SkillTreeVisualizer';
import { VisionBoardModal } from './components/VisionBoardModal';
import { CrossroadsDebater } from './components/CrossroadsDebater';
import { MirrorAnalysisModal } from './components/MirrorAnalysisModal';
import { ValueAuctionModal } from './components/ValueAuctionModal';
import { GlobalMapperModal } from './components/GlobalMapperModal';
import { SkillsGapModal } from './components/SkillsGapModal';
import { MorningManifestoModal } from './components/MorningManifestoModal';
import { DayInLifeSimulator } from './components/DayInLifeSimulator';
import { LoginPage } from './components/LoginPage';
import { LoadingProgressBar } from './components/LoadingProgressBar';
import { XPToast } from './components/XPToast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { KaiPresenceProvider, useKai } from './contexts/KaiPresenceContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { KaiAvatar } from './components/KaiAvatar';
import { MainLayout, AppSection } from './components/MainLayout';
import { Message, SkillNode, MirrorAnalysis, GlobalOpportunityResult, SkillsGapResult, CareerPrototypeDoc, MessageAction } from './types';
import { generateCareerSuggestions, generateSkillTree, generateVisionBoardImage, findGlobalOpportunities, bridgeSkillsGap, generateMorningManifesto, generateDaySimulation } from './services/geminiService';
import { KaiLiveSession } from './services/liveService';
import { LayoutDashboard, MessageSquareText, AlertCircle, X, Cloud, Hourglass, Brain, Sun, Gavel, ScanFace, Scale, Loader2 } from 'lucide-react';
import { useIkigaiLogic } from './hooks/useIkigaiLogic';
import { saveCareerPrototype, updatePrototypeModule } from './services/dbService';
import LifeOSView from './components/LifeOSView';
import { APP_CONFIG } from './config';

// Lazy Load Components
const FeaturesSection = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/FeaturesSection').then(module => {
                resolve({ default: module.FeaturesSection });
            });
        }, 1500);
    })
);

const ProfileView = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/ProfileView').then(module => {
                resolve({ default: module.ProfileView });
            });
        }, 1500);
    })
);

const JourneyView = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/JourneyView').then(module => {
                resolve({ default: module.JourneyView });
            });
        }, 1500);
    })
);

const CareerInsightsView = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/CareerInsightsView').then(module => {
                resolve({ default: module.CareerInsightsView });
            });
        }, 1500);
    })
);

const KaiSettingsView = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/KaiSettingsView').then(module => {
                resolve({ default: module.KaiSettingsView });
            });
        }, 1000);
    })
);

const DocumentLibrary = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/DocumentLibrary').then(module => {
                resolve({ default: module.DocumentLibrary });
            });
        }, 800);
    })
);

const VennView = React.lazy(() =>
    new Promise<{ default: React.ComponentType<any> }>(resolve => {
        setTimeout(() => {
            import('./components/VennView').then(module => {
                resolve({ default: module.VennView });
            });
        }, 800);
    })
);

const AppContent: React.FC = () => {
    const { user, loading: authLoading, logout, deleteAccount } = useAuth();
    const { state: kaiState } = useKai();
    const { theme, getKaiTheme } = useTheme();

    // --- Use Business Logic Hook ---
    const {
        chatMode,
        messages,
        timeCapsuleMessages,
        originStoryMessages,
        ikigaiData,
        userFacts,
        userStats,
        xpNotification,
        setXpNotification,
        careerPaths,
        careerPrototypes,
        currentQuest,
        isQuestCompleted,
        journeyHistory,
        isLoading,
        isDataHydrating,
        saveStatus,
        errorToast,
        // setIkigaiData, // Removed unused
        setCareerPrototypes,
        setMessages,
        setIsQuestCompleted,
        setErrorToast,
        updatePrototypeState,
        removeFact,
        handleSendMessage,
        handleModeChange,
        handleResetData,
        handleDeleteAccount,
        handleUpdateSkillNode,
        mergeData,
        saveJourneyItem,
        updateMessage
    } = useIkigaiLogic({ user, logout, deleteAccount });

    // --- UI State ---
    const [osMode, setOsMode] = useState<'v1' | 'v2'>('v1'); // NEW: Dual Boot State
    const [activeSection, setActiveSection] = useState<AppSection>('chat');
    const [mobileTab, setMobileTab] = useState<'chat' | 'dashboard'>('chat');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isVoiceMode, setIsVoiceMode] = useState(false);
    const [audioAnalyser, setAudioAnalyser] = useState<AnalyserNode | null>(null);

    // Feature States
    const [isSynthesizing, setIsSynthesizing] = useState(false);
    const [activeSkillTree, setActiveSkillTree] = useState<{ tree: SkillNode, careerTitle: string } | null>(null);
    const [isGeneratingTree, setIsGeneratingTree] = useState<boolean>(false);
    const [visionImage, setVisionImage] = useState<string | null>(null);
    const [generatingVisionCareer, setGeneratingVisionCareer] = useState<string | null>(null); // Replaced visualizingCareer
    const [showVisionModal, setShowVisionModal] = useState(false); // NEW: Explicit modal control
    const [currentCareerTitle, setCurrentCareerTitle] = useState<string | null>(null);
    const [isDebaterOpen, setIsDebaterOpen] = useState(false);
    const [isMirrorOpen, setIsMirrorOpen] = useState(false);
    const [isAuctionOpen, setIsAuctionOpen] = useState(false);
    const [globalMapperData, setGlobalMapperData] = useState<{ title: string, data: GlobalOpportunityResult } | null>(null);
    const [isFindingHubs, setIsFindingHubs] = useState<string | null>(null);
    const [skillsGapData, setSkillsGapData] = useState<{ title: string, data: SkillsGapResult } | null>(null);
    const [isBridgingGap, setIsBridgingGap] = useState<string | null>(null);
    const [manifestoData, setManifestoData] = useState<{ script: string, audioData: string } | null>(null);
    const [isGeneratingManifesto, setIsGeneratingManifesto] = useState(false);
    // Simulation
    const [activeSimulation, setActiveSimulation] = useState<any | null>(null);
    const [simulatingCareer, setSimulatingCareer] = useState<string | null>(null);

    // --- Minimum Splash Screen Timer ---
    // Skip splash if we just deleted an account (to show success message immediately)
    const [showSplash, setShowSplash] = useState(() => {
        return !localStorage.getItem('ikigai_account_deleted');
    });

    // Get dynamic theme based on Kai's personality
    const kaiTheme = getKaiTheme(kaiState.color);
    const toolBtnStyle = () => `${theme.colors.text.secondary} hover:${kaiTheme.bgLight} hover:${kaiTheme.text} transition-colors p-1.5 rounded-lg`;

    useEffect(() => {
        if (showSplash) {
            const timer = setTimeout(() => {
                setShowSplash(false);
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [showSplash]);

    const liveSessionRef = useRef<KaiLiveSession | null>(null);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => console.error(err));
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    // --- Feature Handlers ---
    const handleSynthesize = useCallback(async () => {
        setIsSynthesizing(true);
        // setKaiMood('thinking'); // Using context inside hook now if needed, but keeping local UI logic clean
        try {
            const paths = await generateCareerSuggestions(ikigaiData);

            const newPrototypes: CareerPrototypeDoc[] = paths.map(p => ({
                id: p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                title: p.title,
                description: p.description,
                matchScore: p.matchScore,
                reasoning: p.reasoning,
                createdAt: Date.now()
            }));

            // UPDATE: Merge new prototypes into existing state (Newest first)
            setCareerPrototypes(prev => {
                // Remove any from 'prev' that have the same ID as 'new' to avoid duplicates
                // (Effectively updating them with fresh AI generation)
                const newIds = new Set(newPrototypes.map(p => p.id));
                const filteredOld = prev.filter(p => !newIds.has(p.id));
                return [...newPrototypes, ...filteredOld];
            });

            // Persist to DB
            if (user?.uid) {
                for (const p of paths) {
                    await saveCareerPrototype(user.uid, p);
                }
            }

            // Log to Journey
            saveJourneyItem({
                type: 'milestone',
                title: 'Career Prototypes Generated',
                data: { count: paths.length },
                tags: ['career', 'synthesis']
            });

        } catch (e) {
            console.error("Synthesis error:", e);
            setErrorToast("Failed to generate career prototypes.");
        } finally {
            setIsSynthesizing(false);
        }
    }, [ikigaiData, setCareerPrototypes, setErrorToast, saveJourneyItem, user]);

    // Handle Actions from Chat Buttons
    const handleChatAction = useCallback(async (action: MessageAction, messageId: string) => {
        if (action === 'generate_blueprints') {
            await handleSynthesize();
            // Permanently update the message to show "View" button instead of "Generate"
            updateMessage(messageId, { action: 'view_blueprints' });
            setActiveSection('insights');
        } else if (action === 'view_blueprints') {
            setActiveSection('insights');
        }
    }, [handleSynthesize, updateMessage]);

    const handleViewSkillTree = useCallback(async (careerTitle: string) => {
        // Check local state first (caching)
        const existing = careerPrototypes.find(p => p.title === careerTitle)?.skillTree;
        if (existing) {
            setActiveSkillTree({ tree: existing.data, careerTitle });
            return;
        }

        setIsGeneratingTree(true);
        try {
            const tree = await generateSkillTree(careerTitle);
            if (tree) {
                setActiveSkillTree({ tree, careerTitle });
                // Save to Prototype Doc
                if (user?.uid) {
                    await updatePrototypeModule(user.uid, careerTitle, 'skillTree', tree);
                    updatePrototypeState(careerTitle, { skillTree: { data: tree, generatedAt: Date.now() } });
                }
            }
            else setErrorToast("Failed to generate skill tree.");
        } catch (e) {
            setErrorToast("Connection error.");
        } finally {
            setIsGeneratingTree(false);
        }
    }, [setErrorToast, careerPrototypes, user, updatePrototypeState]);

    const handleVisualize = useCallback(async (proto: any) => {
        // Check cache
        const existing = careerPrototypes.find(p => p.title === proto.title)?.visionBoard;
        if (existing) {
            setVisionImage(existing.imageUrl);
            setCurrentCareerTitle(proto.title);
            setShowVisionModal(true); // Explicitly open modal only on cache hit
            return;
        }

        // If not cached, GENERATE ONLY (don't open modal yet)
        setGeneratingVisionCareer(proto.title);
        try {
            const image = await generateVisionBoardImage(proto.title, proto.description);
            if (image) {
                // We do NOT set visionImage or open modal here. 
                // We just save it to the prototype/DB. 
                // The CareerInsightsView will detect the update via careerPrototypes and show the image in the card.

                if (typeof image === 'string') {
                    // Save to Journey!
                    saveJourneyItem({
                        type: 'artifact',
                        subtype: 'vision_board',
                        title: `Visualized ${proto.title}`,
                        data: { imageUrl: image, career: proto.title },
                        tags: ['vision', 'career']
                    });

                    // Save to Prototype
                    if (user?.uid) {
                        try {
                            await updatePrototypeModule(user.uid, proto.title, 'visionBoard', image);
                            updatePrototypeState(proto.title, { visionBoard: { imageUrl: image, generatedAt: Date.now() } });
                        } catch (err) {
                            console.error("DEBUG: Failed to save vision board to DB", err);
                            setErrorToast("Vision generated but failed to save.");
                        }
                    }
                } else {
                    console.error("DEBUG: CRITICAL - Image is not a string, aborting save!", image);
                    setErrorToast("Generated data was invalid.");
                }

            } else setErrorToast("Failed to generate vision.");
        } catch (e) {
            setErrorToast("Error connecting to image service.");
        } finally {
            setGeneratingVisionCareer(null);
        }
    }, [setErrorToast, saveJourneyItem, careerPrototypes, user, updatePrototypeState]);

    const handleFindHubs = useCallback(async (careerTitle: string) => {
        const existing = careerPrototypes.find(p => p.title === careerTitle)?.globalHubs;
        if (existing) {
            setGlobalMapperData({ title: careerTitle, data: existing.data });
            return;
        }

        setIsFindingHubs(careerTitle);
        try {
            const result = await findGlobalOpportunities(careerTitle, ikigaiData.mission);
            if (result) {
                setGlobalMapperData({ title: careerTitle, data: result });
                if (user?.uid) {
                    await updatePrototypeModule(user.uid, careerTitle, 'globalHubs', result);
                    updatePrototypeState(careerTitle, { globalHubs: { data: result, generatedAt: Date.now() } });
                }
            }
            else setErrorToast("Could not find opportunities.");
        } catch (e) {
            setErrorToast("Error connecting to Maps.");
        } finally {
            setIsFindingHubs(null);
        }
    }, [ikigaiData.mission, setErrorToast, careerPrototypes, user, updatePrototypeState]);

    const handleBridgeGap = useCallback(async (careerTitle: string, specificSkill?: string) => {
        // Only cache if general bridge (not specific skill search)
        if (!specificSkill) {
            const existing = careerPrototypes.find(p => p.title === careerTitle)?.gapAnalysis;
            if (existing) {
                setSkillsGapData({ title: careerTitle, data: existing.data });
                return;
            }
        }

        setIsBridgingGap(specificSkill || careerTitle);
        try {
            const result = await bridgeSkillsGap(careerTitle, ikigaiData.vocation, specificSkill);
            if (result) {
                setSkillsGapData({ title: specificSkill ? `Resources for ${specificSkill}` : careerTitle, data: result });
                // Only save general analysis
                if (!specificSkill && user?.uid) {
                    await updatePrototypeModule(user.uid, careerTitle, 'gapAnalysis', result);
                    updatePrototypeState(careerTitle, { gapAnalysis: { data: result, generatedAt: Date.now() } });
                }
            }
            else setErrorToast("Could not identify resources.");
        } catch (e) {
            setErrorToast("Error connecting to Search.");
        } finally {
            setIsBridgingGap(null);
        }
    }, [ikigaiData.vocation, setErrorToast, careerPrototypes, user, updatePrototypeState]);

    const handleSimulate = useCallback(async (careerTitle: string) => {
        const existing = careerPrototypes.find(p => p.title === careerTitle)?.simulation;
        if (existing) {
            setActiveSimulation(existing.data);
            return;
        }

        setSimulatingCareer(careerTitle);
        try {
            const sim = await generateDaySimulation(careerTitle);
            setActiveSimulation(sim);
            if (sim && user?.uid) {
                await updatePrototypeModule(user.uid, careerTitle, 'simulation', sim);
                updatePrototypeState(careerTitle, { simulation: { data: sim, generatedAt: Date.now() } });
            }
        } catch (e) {
            console.error("Simulation failed", e);
            setErrorToast("Simulation failed");
        } finally {
            setSimulatingCareer(null);
        }
    }, [careerPrototypes, user, updatePrototypeState, setErrorToast]);

    const handleGenerateManifesto = useCallback(async () => {
        setIsGeneratingManifesto(true);
        try {
            const result = await generateMorningManifesto(ikigaiData, currentQuest);
            if (result) {
                setManifestoData(result);
                // Save to Journey
                saveJourneyItem({
                    type: 'artifact',
                    subtype: 'manifesto',
                    title: 'Morning Manifesto Created',
                    data: { script: result.script }, // Don't save audio blob to DB to save space, just script
                    tags: ['motivation']
                });
            }
            else setErrorToast("Could not generate manifesto.");
        } catch (e) {
            setErrorToast("Error creating audio.");
        } finally {
            setIsGeneratingManifesto(false);
        }
    }, [ikigaiData, currentQuest, setErrorToast, saveJourneyItem]);

    const handleMergeMirrorData = useCallback((data: MirrorAnalysis) => {
        mergeData(data);
        const sysMsg: Message = {
            id: Date.now().toString(),
            role: 'model',
            text: "I've analyzed your text and uncovered some hidden gems! I've added them to your dashboard."
        };
        setMessages(prev => [...prev, sysMsg]);
        // setKaiMood('excited'); // Hook logic handles this now if integrated

        saveJourneyItem({
            type: 'milestone',
            title: '360° Mirror Analysis',
            data: { analysis: data },
            tags: ['reflection']
        });
    }, [mergeData, setMessages, saveJourneyItem]);

    // View Artifact Helper for Journey Timeline
    const handleViewArtifact = (entry: any) => {
        if (entry.subtype === 'vision_board' && entry.data?.imageUrl) {
            setVisionImage(entry.data.imageUrl);
            setCurrentCareerTitle(entry.data.career || 'Vision Board');
            setShowVisionModal(true);
        }
    };

    // --- Voice Mode ---
    const startVoiceMode = useCallback(async () => {
        if (!process.env.API_KEY) {
            setErrorToast("API Key is missing.");
            return;
        }
        setErrorToast(null);
        setIsVoiceMode(true);

        liveSessionRef.current = new KaiLiveSession(process.env.API_KEY, {
            onDashboardUpdate: (updates) => mergeData(updates),
            onClose: () => {
                setIsVoiceMode(false);
                setAudioAnalyser(null);
            },
            onError: (err) => {
                setIsVoiceMode(false);
                setAudioAnalyser(null);
                setErrorToast(err.message || "Voice Mode Error");
            }
        });

        await liveSessionRef.current.connect();
        setAudioAnalyser(liveSessionRef.current.getOutputAnalyser());
    }, [mergeData, setErrorToast]);

    const endVoiceMode = useCallback(() => {
        if (liveSessionRef.current) {
            liveSessionRef.current.disconnect();
            liveSessionRef.current = null;
        }
        setAudioAnalyser(null);
        setIsVoiceMode(false);
    }, []);

    const getWhisperMessage = useCallback(() => {
        let currentMessages: Message[] = [];
        if (chatMode === 'standard') currentMessages = messages;
        else if (chatMode === 'time_capsule') currentMessages = timeCapsuleMessages;
        else if (chatMode === 'origin_story') currentMessages = originStoryMessages;

        const lastModelMsg = [...currentMessages].reverse().find(m => m.role === 'model');

        if (lastModelMsg) {
            const text = lastModelMsg.text.replace(/[\*_]/g, '').trim();
            const questions = text.match(/[^.!?\n]+[?]/g);
            if (questions && questions.length > 0) {
                const lastQuestion = questions[questions.length - 1].trim();
                if (lastQuestion.length < 90) return lastQuestion;
            }
            const sentences = text.match(/[^.!?\n]+[.!?]/g);
            if (sentences && sentences.length > 0) {
                const lastSentence = sentences[sentences.length - 1].trim();
                if (lastSentence.length < 80) return lastSentence;
            }
            return text.length > 60 ? text.substring(0, 57) + "..." : text;
        }
        return "What do you love to do nowadays?";
    }, [chatMode, messages, timeCapsuleMessages, originStoryMessages]);

    const getLoadingState = () => {
        switch (activeSection) {
            case 'profile':
                return {
                    title: "Accessing User Profile",
                    status: "Decrypting Data",
                    message: "Retrieving your account settings and secure preferences..."
                };
            case 'features':
                return {
                    title: "Initializing Features",
                    status: "Loading Assets",
                    message: "Preparing advanced AI tools and visualizations..."
                };
            case 'journey':
                return {
                    title: "Constructing Timeline",
                    status: "Loading Journey",
                    message: "Retrieving your quests, artifacts, and milestones..."
                };
            case 'insights':
                return {
                    title: "Opening Dossier",
                    status: "Loading Personas",
                    message: "Compiling intelligence on your potential futures..."
                };
            case 'kai-settings':
                return {
                    title: "Connecting to Kai Core",
                    status: "Establishing Link",
                    message: "Syncing resonance frequencies..."
                };
            case 'library':
                return {
                    title: "Opening Archive",
                    status: "Indexing Knowledge",
                    message: "Retrieving system documentation..."
                };
            case 'venn':
                return {
                    title: "Aligning Pillars",
                    status: "Calculating Intersections",
                    message: "Mapping your life data to the Ikigai framework..."
                };
            default:
                return {}; // Default loading state
        }
    };

    // --- Rendering ---

    // Consolidate loading logic: Auth Check OR Data Syncing OR Forced Splash Delay
    if (authLoading || isDataHydrating || showSplash) {
        return (
            <LoadingProgressBar
                variant="fullscreen"
                title="Synchronizing Kai"
                status={authLoading ? "Authenticating" : "Retrieving Profile"}
                message="Loading your personal Ikigai data..."
            />
        );
    }

    if (!user) return <LoginPage />;

    const renderChatSection = () => {
        // v2 Mode: Life OS View (Full Screen Split)
        if (osMode === 'v2' && APP_CONFIG.FEATURES.ENABLE_LIFE_OS_V2) {
            return (
                <div className="h-full w-full flex flex-col">
                    {/* OS Switcher Header */}
                    <div className="h-12 bg-[#050510] border-b border-gray-800 flex items-center justify-between px-4 shrink-0">
                        <span className="text-gray-400 text-xs tracking-wider font-mono">KAI_OS_KERNEL_V2.0</span>
                        <button
                            onClick={() => setOsMode('v1')}
                            className="bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1 rounded text-xs font-bold transition-colors border border-gray-700"
                        >
                            SWITCH TO CLASSIC (v1)
                        </button>
                    </div>
                    {/* The v2 Layout */}
                    <div className="flex-1 overflow-hidden">
                        <LifeOSView
                            chatState={{
                                messages: chatMode === 'standard' ? messages : chatMode === 'time_capsule' ? timeCapsuleMessages : originStoryMessages,
                                isThinking: isLoading,
                                onSendMessage: handleSendMessage
                            }}
                        />
                    </div>
                </div>
            );
        }

        // v1 Mode: Classic Dashboard
        return (
            <div className="flex flex-col h-full">
                {/* OS Switcher Header (v1) */}
                {APP_CONFIG.FEATURES.ENABLE_LIFE_OS_V2 && (
                    <div className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-4 py-2 flex justify-end">
                        <button
                            onClick={() => setOsMode('v2')}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-xs font-bold transition-colors shadow-lg flex items-center gap-2"
                        >
                            <Brain size={12} />
                            BOOT LIFE OS (v2)
                        </button>
                    </div>
                )}

                <div className={`md:hidden flex border-b shrink-0 ${theme.colors.surface} ${theme.colors.divider}`}>
                    <button onClick={() => setMobileTab('chat')} className={`flex-1 py-3 text-sm font-medium flex gap-2 justify-center ${mobileTab === 'chat' ? `${theme.colors.primary} border-b-2 border-indigo-600` : theme.colors.text.secondary}`}>
                        <MessageSquareText size={16} /> Conversation
                    </button>
                    <button onClick={() => setMobileTab('dashboard')} className={`flex-1 py-3 text-sm font-medium flex gap-2 justify-center ${mobileTab === 'dashboard' ? `${theme.colors.primary} border-b-2 border-indigo-600` : theme.colors.text.secondary}`}>
                        <LayoutDashboard size={16} /> Ikigai Board
                    </button>
                </div>

                <div className="flex-1 flex flex-col md:flex-row md:gap-4 w-full h-full overflow-hidden p-2 md:p-0">
                    <div className={`flex-1 h-full min-w-0 transition-all duration-300 ${mobileTab === 'chat' ? 'block' : 'hidden md:block'}`}>
                        <ChatInterface
                            messages={chatMode === 'standard' ? messages : chatMode === 'time_capsule' ? timeCapsuleMessages : originStoryMessages}
                            onSendMessage={handleSendMessage}
                            isLoading={isLoading}
                            onStartVoice={startVoiceMode}
                            chatMode={chatMode}
                            onModeChange={handleModeChange}
                            onAction={handleChatAction} // NEW HANDLER
                        />
                    </div>

                    <div className={`flex-1 h-full min-w-0 transition-all duration-300 ${mobileTab === 'dashboard' ? 'block' : 'hidden md:block'}`}>
                        <div className={`h-full md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden transition-colors duration-500 ${chatMode === 'time_capsule'
                            ? (theme.mode === 'dark' ? 'bg-slate-900 border border-indigo-900' : 'bg-slate-50 border border-indigo-100')
                            : theme.colors.chat.standard.container
                            }`}>

                            {/* DASHBOARD HEADER WITH TOOLS */}
                            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${chatMode === 'time_capsule'
                                ? (theme.mode === 'dark' ? 'border-indigo-900/50' : 'border-indigo-100')
                                : `${theme.colors.chat.standard.header} border-indigo-50/50`
                                }`}>

                                {/* LEFT GROUP: Title & Status */}
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <LayoutDashboard className={`shrink-0 ${chatMode === 'time_capsule' ? (theme.mode === 'dark' ? 'text-violet-400' : 'text-violet-600') : kaiTheme.text}`} size={20} />
                                        <h2 className={`font-bold hidden md:block ${chatMode === 'time_capsule'
                                            ? (theme.mode === 'dark' ? 'text-white' : 'text-slate-800')
                                            : theme.colors.text.main
                                            }`}>
                                            Ikigai Dashboard
                                        </h2>
                                        <h2 className={`font-bold md:hidden ${chatMode === 'time_capsule'
                                            ? (theme.mode === 'dark' ? 'text-white' : 'text-slate-800')
                                            : theme.colors.text.main
                                            }`}>
                                            Dashboard
                                        </h2>
                                        {saveStatus === 'saving' && <Cloud size={14} className="text-gray-400 animate-pulse ml-1" />}
                                        {saveStatus === 'saved' && <Cloud size={14} className="text-green-500 ml-1" />}
                                    </div>

                                    {/* Divider */}
                                    <div className={`h-4 w-px ${theme.mode === 'dark' ? 'bg-white/10' : 'bg-black/10'}`}></div>

                                    {/* Status Badge */}
                                    {chatMode === 'time_capsule' ? (
                                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${theme.mode === 'dark' ? 'text-violet-300 bg-violet-900/30 border-violet-500/20' : 'text-violet-700 bg-violet-50 border-violet-200'}`}>
                                            Read Only
                                        </span>
                                    ) : (
                                        <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${theme.mode === 'dark' ? 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                                            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                                            <span className="hidden sm:inline">Live Updates</span>
                                            <span className="sm:hidden">Live</span>
                                        </span>
                                    )}
                                </div>

                                {/* RIGHT GROUP: Toolbar (Injected Here) */}
                                {chatMode !== 'time_capsule' && (
                                    <div className={`flex items-center gap-1 p-1 rounded-2xl border shadow-lg backdrop-blur-md transition-colors ${theme.mode === 'dark' ? 'bg-black/20 border-white/10' : 'bg-white/60 border-white/40'}`}>
                                        <button onClick={handleGenerateManifesto} disabled={isGeneratingManifesto} className={toolBtnStyle()} title="Manifesto">
                                            {isGeneratingManifesto ? <Loader2 size={16} className="animate-spin" /> : <Sun size={16} />}
                                        </button>
                                        <button onClick={() => setIsAuctionOpen(true)} className={toolBtnStyle()} title="Auction">
                                            <Gavel size={16} />
                                        </button>
                                        <button onClick={() => setIsMirrorOpen(true)} className={toolBtnStyle()} title="Mirror">
                                            <ScanFace size={16} />
                                        </button>
                                        <button onClick={() => setIsDebaterOpen(true)} className={toolBtnStyle()} title="Debater">
                                            <Scale size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="relative flex-1 overflow-hidden">
                                {chatMode === 'time_capsule' && (
                                    <div className={`absolute inset-0 z-10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none ${theme.mode === 'dark' ? 'bg-slate-900/60' : 'bg-white/60'}`}>
                                        <div className="text-center p-6">
                                            <Hourglass size={48} className="text-violet-400 mx-auto mb-2 opacity-50" />
                                            <p className={`font-medium ${theme.mode === 'dark' ? 'text-violet-200' : 'text-violet-800'}`}>Dashboard Locked</p>
                                            <p className={`text-xs ${theme.mode === 'dark' ? 'text-violet-300/60' : 'text-violet-600/60'}`}>Future You already has everything needed.</p>
                                        </div>
                                    </div>
                                )}
                                <div className={`h-full w-full ${chatMode === 'time_capsule' ? 'opacity-30 pointer-events-none' : ''}`}>
                                    <IkigaiDashboard
                                        data={ikigaiData}
                                        careerPaths={careerPaths}
                                        onSynthesize={handleSynthesize}
                                        isSynthesizing={isSynthesizing}
                                        currentQuest={currentQuest}
                                        isQuestCompleted={isQuestCompleted}
                                        onToggleQuest={() => setIsQuestCompleted(!isQuestCompleted)}
                                        // Handlers triggered from Dashboard still work, but ideally user goes to Insights view for full data
                                        onViewSkillTree={handleViewSkillTree}
                                        isGeneratingTree={isGeneratingTree}
                                        onVisualize={handleVisualize}
                                        visualizingCareer={generatingVisionCareer}
                                        onFindHubs={handleFindHubs}
                                        isFindingHubs={isFindingHubs}
                                        onBridgeGap={handleBridgeGap}
                                        isBridgingGap={isBridgingGap}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <MainLayout
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            user={user}
            logout={logout}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            userStats={userStats} // Pass stats to layout
        >
            <Suspense fallback={<div className="h-full w-full"><LoadingProgressBar {...getLoadingState()} /></div>}>
                {activeSection === 'chat' && renderChatSection()}
                {activeSection === 'features' && (
                    <FeaturesSection
                        onNavigateToChat={() => setActiveSection('chat')}
                        journeyHistory={journeyHistory}
                        careerPrototypes={careerPrototypes}
                    />
                )}
                {activeSection === 'journey' && <JourneyView history={journeyHistory} onViewArtifact={handleViewArtifact} />}
                {activeSection === 'profile' && (
                    <ProfileView
                        user={user}
                        journeyHistory={journeyHistory}
                        userFacts={userFacts} // Pass facts
                        onRemoveFact={removeFact} // Pass handler
                        onLogout={logout}
                        onResetData={handleResetData}
                        onDeleteAccount={handleDeleteAccount}
                    />
                )}
                {activeSection === 'kai-settings' && <KaiSettingsView />}
                {activeSection === 'library' && <DocumentLibrary />}
                {activeSection === 'insights' && (
                    <CareerInsightsView
                        careerPrototypes={careerPrototypes}
                        ikigaiData={ikigaiData}
                        onSimulate={handleSimulate}
                        onViewSkillTree={handleViewSkillTree}
                        onVisualize={handleVisualize}
                        onFindHubs={handleFindHubs}
                        onBridgeGap={handleBridgeGap}
                        simulatingCareer={simulatingCareer}
                        generatingVisionCareer={generatingVisionCareer}
                        isGeneratingTree={isGeneratingTree}
                        isFindingHubs={isFindingHubs}
                        isBridgingGap={isBridgingGap}
                    />
                )}
                {activeSection === 'venn' && <VennView data={ikigaiData} />}
            </Suspense>

            <KaiAvatar whisper={getWhisperMessage()} />

            <VoiceOverlay
                isActive={isVoiceMode}
                onClose={endVoiceMode}
                audioAnalyser={audioAnalyser}
            />

            {/* Feature Modals - Triggered globally */}
            {/* Logic: Show modal ONLY if explicit flag is true AND we have an image. */}
            {showVisionModal && visionImage && currentCareerTitle && (
                <VisionBoardModal
                    imageUrl={visionImage}
                    careerTitle={currentCareerTitle}
                    onClose={() => {
                        setShowVisionModal(false);
                        setVisionImage(null);
                        setCurrentCareerTitle(null);
                    }}
                />
            )}

            {activeSkillTree && (
                <SkillTreeVisualizer
                    data={activeSkillTree.tree}
                    onClose={() => setActiveSkillTree(null)}
                    onBridgeGap={(_, skill) => handleBridgeGap(activeSkillTree.careerTitle, skill)}
                    onUpdateStatus={(nodeId, status) => handleUpdateSkillNode(activeSkillTree.careerTitle, nodeId, status)}
                    isLoadingResources={!!isBridgingGap}
                />
            )}

            {activeSimulation && (
                <DayInLifeSimulator
                    simulation={activeSimulation}
                    onClose={() => setActiveSimulation(null)}
                />
            )}

            {isDebaterOpen && <CrossroadsDebater onClose={() => setIsDebaterOpen(false)} />}

            {isMirrorOpen && <MirrorAnalysisModal onClose={() => setIsMirrorOpen(false)} onMerge={handleMergeMirrorData} />}

            {isAuctionOpen && <ValueAuctionModal onClose={() => setIsAuctionOpen(false)} />}

            {globalMapperData && (
                <GlobalMapperModal
                    careerTitle={globalMapperData.title}
                    data={globalMapperData.data}
                    onClose={() => setGlobalMapperData(null)}
                />
            )}

            {skillsGapData && (
                <SkillsGapModal
                    careerTitle={skillsGapData.title}
                    data={skillsGapData.data}
                    onClose={() => setSkillsGapData(null)}
                />
            )}

            {manifestoData && (
                <MorningManifestoModal
                    script={manifestoData.script}
                    audioData={manifestoData.audioData}
                    onClose={() => setManifestoData(null)}
                />
            )}

            {/* Notifications */}
            {errorToast && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-full shadow-xl z-[100] animate-fade-in-up flex items-center gap-2">
                    <AlertCircle size={18} />
                    <span className="text-sm font-bold">{errorToast}</span>
                    <button onClick={() => setErrorToast(null)} className="ml-2 hover:bg-white/20 rounded-full p-1"><X size={14} /></button>
                </div>
            )}

            <XPToast
                notification={xpNotification}
                onClose={() => setXpNotification(null)}
            />
        </MainLayout>
    );
};

export default function App() {
    return (
        <AuthProvider>
            <KaiPresenceProvider>
                <ThemeProvider>
                    <AppContent />
                </ThemeProvider>
            </KaiPresenceProvider>
        </AuthProvider>
    );
}